import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Select,
  Stack,
  Button,
  Group,
  Paper,
  NumberInput,
  Alert,
  LoadingOverlay,
  Grid,
  Box,
} from "@mantine/core";
import { DateInput, TimePicker } from "@mantine/dates";
import { IconAlertCircle } from "@tabler/icons-react";
import moment from "moment-timezone";
import { useSalon } from "../../../../../../context/SalonContext";
import { useCalendar } from "../../../../../../context/CalendarContext";
import { useServiceAdjustmentForms } from "../../../../../../context/ServiceAdjustmentFormsContext";
import {
  GetTransactionsResponse,
  TransactionResponse,
  BookedService,
  EditTransactionLineItemsRequest,
} from "../../../../../../types/transactions";
import { BusinessInfo } from "../../../../../../types/context";
import {
  SalonService,
  Employee,
  StaffServiceMapping,
} from "../../../../../../types/salon";
import { useResponsive } from "../../../../../../context/ResponsiveContext";
import AdditionalQuestionsSection from "./AdditionalQuestionsSection";
import { filterBookableActiveStaff } from "../../../../../../utils/staffUtils";
import { generateTimePresets } from "../../../../../../utils/timePresets";
import {
  isPerUnitBasis,
  unitLabelForBasis,
} from "../../../../../../utils/quantityUtils";

interface TransactionBookedServiceModalProps {
  mode: "add" | "edit";
  service?: BookedService; // Required for edit mode, optional for add mode
  transaction: GetTransactionsResponse | TransactionResponse;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  businessInfo: BusinessInfo | null;
}

interface FormData {
  staffId: string;
  serviceId: string;
  date: string | null;
  startTime: string;
  baseDuration: number;
  processingTime: number;
  finishingTime: number;
  bufferTime: number;
  price: number;
  quantity: number;
}

export default function TransactionBookedServiceModal({
  mode,
  service,
  transaction,
  onSubmit,
  onCancel,
  businessInfo,
}: TransactionBookedServiceModalProps) {
  const { isMobile, isTablet } = useResponsive();
  const { employees, services } = useSalon();
  const { staffServices } = useCalendar();
  const { adjustmentForms, formJunctions } = useServiceAdjustmentForms();
  const [availableServices, setAvailableServices] = useState<SalonService[]>(
    services || [],
  );

  const [availableStaff, setAvailableStaff] = useState<Employee[]>(() =>
    filterBookableActiveStaff(
      employees,
      mode === "edit" && service ? service.staff_id || "" : "",
    ),
  );

  // Type assertions for context values since they come from untyped contexts
  const typedStaffServices = staffServices as StaffServiceMapping[] | undefined;
  const typedServices = services as SalonService[] | undefined;
  const typedEmployees = employees as Employee[] | undefined;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate time presets for TimePicker
  const timePresets = generateTimePresets();

  // Controlled dropdown state for start time picker
  const [startTimeDropdownOpened, setStartTimeDropdownOpened] = useState(false);
  // Track if change came from a mouse click (preset) vs keyboard (manual typing)
  const startTimeMouseDownRef = useRef(false);

  // Update available staff when employees load or change
  useEffect(() => {
    if (!formData.serviceId) {
      // Only update if no service is selected (service selection has its own filtering)
      setAvailableStaff(filterBookableActiveStaff(employees, formData.staffId));
    }
  }, [typedEmployees]);

  // State for service adjustment forms
  const [selectedAdjustmentOptions, setSelectedAdjustmentOptions] = useState<
    string[]
  >([]);
  const [isAdditionalQuestionsExpanded, setIsAdditionalQuestionsExpanded] =
    useState(false);

  const timezone = businessInfo?.timezone || "UTC";

  // Initialize form data based on mode
  // Set price to unit_price, since we will attach the service adjustment form option deltas to the price
  const [formData, setFormData] = useState<FormData>({
    staffId: mode === "edit" && service ? service.staff_id || "" : "",
    serviceId: mode === "edit" && service ? service.salon_service_id || "" : "",
    date:
      mode === "edit" && service && service.start_dt
        ? moment.utc(service.start_dt).tz(timezone).format("YYYY-MM-DD")
        : new Date().toISOString().split("T")[0],
    startTime:
      mode === "edit" && service && service.start_dt
        ? moment.utc(service.start_dt).tz(timezone).format("HH:mm")
        : "",
    baseDuration:
      mode === "edit" && service ? (service.base_duration_mins ?? 30) : 30,
    processingTime:
      mode === "edit" && service ? (service.processing_time_mins ?? 0) : 0,
    finishingTime:
      mode === "edit" && service ? (service.finishing_time_mins ?? 0) : 0,
    bufferTime:
      mode === "edit" && service ? (service.buffer_time_mins ?? 0) : 0,
    price: mode === "edit" && service ? service.unit_price || 0 : 0,
    quantity: mode === "edit" && service ? (service.quantity ?? 1) : 1,
  });

  // Set default values for add mode based on last service in transaction
  useEffect(() => {
    if (mode === "add" && transaction.line_items.booked_services.length > 0) {
      const firstService = transaction.line_items.booked_services[0];
      const lastService =
        transaction.line_items.booked_services[
          transaction.line_items.booked_services.length - 1
        ];

      // Set default staff member to the last service's staff
      if (lastService.staff_id) {
        setFormData((prev) => ({ ...prev, staffId: lastService.staff_id }));
      }

      // Set default date and start time based on last service's end time
      if (lastService.start_dt) {
        const firstServiceStartDate = moment
          .utc(firstService.start_dt)
          .tz(timezone)
          .format("YYYY-MM-DD");
        const lastServiceCalcEndTime = moment
          .utc(lastService.calc__end_dt)
          .tz(timezone)
          .format("HH:mm");

        setFormData((prev) => ({
          ...prev,
          date: firstServiceStartDate,
          startTime: lastServiceCalcEndTime,
        }));
      }
    }
  }, [mode, transaction.line_items.booked_services, timezone]);

  // Filter staff and services based on selection (same logic as AppointmentForm)
  useEffect(() => {
    if (formData.staffId && typedStaffServices && typedServices) {
      const staffServiceIds = typedStaffServices
        .filter(
          (service: StaffServiceMapping) =>
            service.staffId === formData.staffId,
        )
        .map((service: StaffServiceMapping) => service.salonServiceId);

      const filteredServices = typedServices.filter((service: SalonService) =>
        staffServiceIds.includes(service.id),
      );

      setAvailableServices(filteredServices);
    } else if (typedServices) {
      setAvailableServices(typedServices);
    }
  }, [formData.staffId, typedStaffServices, typedServices]);

  useEffect(() => {
    if (formData.serviceId && typedServices && typedEmployees) {
      const selectedService = typedServices.find(
        (s: SalonService) => s.id === formData.serviceId,
      );

      if (selectedService) {
        const availableStaffIds =
          selectedService.servicing_staff?.map(
            (staff: any) => staff.staff_id,
          ) || [];
        const filteredStaff = filterBookableActiveStaff(
          employees,
          formData.staffId,
        ).filter((emp: Employee) => availableStaffIds.includes(emp.id));

        setAvailableStaff(filteredStaff);
      }
    } else if (typedEmployees) {
      setAvailableStaff(filterBookableActiveStaff(employees, formData.staffId));
    }
  }, [formData.serviceId, formData.staffId, typedServices, typedEmployees]);

  // Initialize selected adjustment options from sub_line_items in edit mode
  useEffect(() => {
    if (
      mode === "edit" &&
      service?.sub_line_items &&
      service.sub_line_items.length > 0
    ) {
      // Extract source_id values from sub_line_items to pre-populate selections
      const optionIds = service.sub_line_items.map((item) => item.source_id);
      setSelectedAdjustmentOptions(optionIds);
    }
  }, [mode, service]);

  // Compute active adjustment forms for the selected service
  const activeAdjustmentForms = useMemo(() => {
    if (!formData.serviceId || !adjustmentForms || !formJunctions) {
      return [];
    }

    // Find all junctions for this service
    const serviceJunctions = formJunctions.filter(
      (junction) => junction.salon_service_id === formData.serviceId,
    );

    if (serviceJunctions.length === 0) {
      return [];
    }

    // Get the form IDs from junctions
    const formIds = serviceJunctions.map((j) => j.service_adjustment_form_id);

    // Filter and sort forms
    const activeForms = adjustmentForms
      .filter(
        (form) =>
          form.is_active && formIds.includes(form.service_adjustment_form_id!),
      )
      .sort((a, b) => a.priority - b.priority) // Lower priority = higher up
      .map((form) => ({
        ...form,
        questions: form.questions
          .slice() // Create a copy to avoid mutating original
          .sort((a, b) => a.priority - b.priority)
          .map((question) => ({
            ...question,
            options: question.options
              .slice()
              .sort((a, b) => a.priority - b.priority),
          })),
      }));

    return activeForms;
  }, [formData.serviceId, adjustmentForms, formJunctions]);

  // Helper function to calculate price and duration based on service and staff selection
  const calculatePriceAndDuration = (
    serviceId: string,
    staffId: string,
  ): Partial<FormData> => {
    if (!serviceId || !staffId || !typedServices) {
      return {};
    }

    const selectedService = typedServices.find(
      (s: SalonService) => s.id === serviceId,
    );

    if (!selectedService) {
      return {};
    }

    // Find the staff-specific pricing and duration
    const staffService = selectedService.servicing_staff?.find(
      (ss: any) => ss.staff_id === staffId,
    );

    const price = staffService?.price || selectedService.default_price;
    const baseDuration =
      staffService?.base_duration_mins ??
      selectedService.default_base_duration_mins ??
      30;
    const processingTime =
      staffService?.processing_time_mins ??
      selectedService.default_processing_time_mins ??
      0;
    const finishingTime =
      staffService?.finishing_time_mins ??
      selectedService.default_finishing_time_mins ??
      0;
    const bufferTime =
      staffService?.buffer_time_mins ??
      selectedService.default_buffer_time_mins ??
      0;

    return {
      price: price || 0,
      baseDuration,
      processingTime,
      finishingTime,
      bufferTime,
    };
  };

  const handleStaffSelect = (staffId: string | null) => {
    const newStaffId = staffId || "";
    setErrors((prev) => ({ ...prev, staffId: "" }));

    // If both service and new staff are selected, recalculate price and duration
    if (newStaffId && formData.serviceId) {
      const calculations = calculatePriceAndDuration(
        formData.serviceId,
        newStaffId,
      );
      setFormData((prev) => ({
        ...prev,
        staffId: newStaffId,
        ...calculations,
      }));
    } else {
      setFormData((prev) => ({ ...prev, staffId: newStaffId }));
    }
  };

  const handleServiceSelect = (serviceId: string | null) => {
    const newServiceId = serviceId || "";
    setErrors((prev) => ({ ...prev, serviceId: "" }));
    // Reset adjustment options when service changes
    setSelectedAdjustmentOptions([]);

    // If both new service and staff are selected, recalculate price and duration
    if (newServiceId && formData.staffId) {
      const calculations = calculatePriceAndDuration(
        newServiceId,
        formData.staffId,
      );
      setFormData((prev) => ({
        ...prev,
        serviceId: newServiceId,
        ...calculations,
      }));
    } else {
      setFormData((prev) => ({ ...prev, serviceId: newServiceId }));
    }
  };

  const handleAdjustmentOptionToggle = (
    optionId: string,
    questionId: string,
  ) => {
    setSelectedAdjustmentOptions((prev) => {
      // Find all options for this question across all forms
      const optionsForQuestion: string[] = [];
      activeAdjustmentForms.forEach((form) => {
        form.questions.forEach((question) => {
          if (question.question_id === questionId) {
            question.options.forEach((option) => {
              if (option.option_id) {
                optionsForQuestion.push(option.option_id);
              }
            });
          }
        });
      });

      // Remove any previously selected option from this question
      const withoutCurrentQuestion = prev.filter(
        (id) => !optionsForQuestion.includes(id),
      );

      // If optionId is empty, this is a deselection - don't add anything back
      if (!optionId) {
        return withoutCurrentQuestion;
      }

      // Add the newly selected option
      return [...withoutCurrentQuestion, optionId];
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.staffId) {
      newErrors.staffId = "Staff member is required";
    }
    if (!formData.serviceId) {
      newErrors.serviceId = "Service is required";
    }
    if (!formData.date) {
      newErrors.date = "Date is required";
    }
    if (!formData.startTime) {
      newErrors.startTime = "Start time is required";
    }
    if (formData.baseDuration <= 0) {
      newErrors.baseDuration = "Base duration must be greater than 0";
    }
    if (formData.price == null || formData.price < 0) {
      // Allow $0 for complimentary/free services (e.g. consultations)
      newErrors.price = "Price must be 0 or greater";
    }
    // Per-unit/syringe services carry a units count; a cleared field leaves
    // quantity at 0, which the backend rejects (< 0.01). Surface it inline
    // instead of letting the operator hit a server error on submit.
    const validatingService = (typedServices ?? []).find(
      (s) => s.id === formData.serviceId,
    );
    if (
      isPerUnitBasis(validatingService?.price_basis) &&
      (formData.quantity == null || formData.quantity < 0.01)
    ) {
      newErrors.quantity = "Units must be at least 0.01";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Calculate start and end times
      if (!formData.date) {
        setErrors({ general: "Date is required" });
        return;
      }

      const startDateTime = moment
        .tz(formData.date, "YYYY-MM-DD", timezone)
        .hour(moment(formData.startTime, "HH:mm").hour())
        .minute(moment(formData.startTime, "HH:mm").minute())
        .toDate();

      // Find the staff service ID
      const staffService = typedStaffServices?.find(
        (service: StaffServiceMapping) =>
          service.staffId === formData.staffId &&
          service.salonServiceId === formData.serviceId,
      );

      if (!staffService) {
        setErrors({ general: "Invalid staff-service combination" });
        return;
      }

      if (mode === "edit") {
        // Edit mode: Update existing service
        if (!service) {
          setErrors({ general: "Service data is required for editing" });
          return;
        }

        const lineItemData: EditTransactionLineItemsRequest = {
          transaction_id: transaction.transaction.id,
          line_items: {
            booked_services: [
              {
                id: service.id,
                line_item_id: service.line_item_id,
                staff_service_id: staffService.id,
                unit_price: formData.price,
                start_dt: startDateTime.toISOString(),
                base_duration_mins: formData.baseDuration,
                processing_time_mins: formData.processingTime,
                finishing_time_mins: formData.finishingTime,
                buffer_time_mins: formData.bufferTime,
                service_adjustment_form_option_ids: selectedAdjustmentOptions,
                ...(isPerUnitService ? { quantity: formData.quantity } : {}),
              },
            ],
          },
        };

        onSubmit(lineItemData);
      } else {
        // Add mode: Create new service
        const lineItemData = {
          booked_services: [
            {
              staff_service_id: staffService.id,
              unit_price: formData.price,
              start_dt: startDateTime.toISOString(),
              base_duration_mins: formData.baseDuration,
              processing_time_mins: formData.processingTime,
              finishing_time_mins: formData.finishingTime,
              buffer_time_mins: formData.bufferTime,
              service_adjustment_form_option_ids: selectedAdjustmentOptions,
              ...(isPerUnitService ? { quantity: formData.quantity } : {}),
            },
          ],
        };

        onSubmit(lineItemData);
      }
    } catch (error) {
      const errorMessage =
        mode === "edit"
          ? "Failed to update service. Please try again."
          : "Failed to add service. Please try again.";
      setErrors({ general: errorMessage });
      setIsSubmitting(false);
    }
  };

  const getButtonText = () =>
    mode === "edit" ? "Update Service" : "Add Service";

  // Per-syringe / per-unit services capture a units count; standard
  // (per_service) services are always a single unit and hide the field.
  const selectedSalonService = (typedServices ?? []).find(
    (s) => s.id === formData.serviceId,
  );
  const isPerUnitService = isPerUnitBasis(selectedSalonService?.price_basis);
  const unitsLabel = unitLabelForBasis(
    selectedSalonService?.price_basis,
    formData.quantity,
  );

  return (
    <Paper p="md">
      <LoadingOverlay visible={isSubmitting} />
      <Stack gap="lg">
        {errors.general && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            {errors.general}
          </Alert>
        )}

        <Grid>
          <Grid.Col span={isMobile ? 12 : 6}>
            <DateInput
              label="Date"
              required
              value={formData.date}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, date: value }))
              }
              minDate={new Date()}
              clearable
              placeholder="Select date"
              error={errors.date}
            />
          </Grid.Col>
          <Grid.Col span={isMobile ? 12 : 6}>
            <Box
              onClickCapture={() => setStartTimeDropdownOpened(true)}
              onMouseDownCapture={() => {
                startTimeMouseDownRef.current = true;
              }}
            >
              <TimePicker
                label="Start Time"
                required
                value={formData.startTime}
                onChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    startTime: value,
                  }));
                  if (startTimeMouseDownRef.current) {
                    setStartTimeDropdownOpened(false);
                  }
                  startTimeMouseDownRef.current = false;
                }}
                onFocus={() => setStartTimeDropdownOpened(true)}
                format="12h"
                withSeconds={false}
                minutesStep={5}
                presets={timePresets}
                withDropdown={true}
                popoverProps={{
                  opened: startTimeDropdownOpened,
                  onChange: (_opened) =>
                    !_opened && setStartTimeDropdownOpened(false),
                }}
                maxDropdownContentHeight={300}
                error={errors.startTime}
                styles={(theme) => ({
                  input: {
                    fontSize: "1rem",
                    "&:focus": {
                      borderColor: theme.colors.teal[5],
                    },
                  },
                })}
              />
            </Box>
          </Grid.Col>
        </Grid>

        <Select
          label="Staff Member"
          required
          searchable
          nothingFoundMessage="No staff found"
          placeholder="Select staff member"
          data={
            availableStaff?.map((emp) => ({
              value: emp.id,
              label: `${emp.first_name} ${emp.last_name}`,
            })) || []
          }
          value={formData.staffId}
          onChange={handleStaffSelect}
          error={errors.staffId}
          clearable
          styles={(theme) => ({
            input: {
              "&:focus": {
                borderColor: theme.colors.teal[5],
              },
            },
          })}
        />

        <Select
          label="Service"
          required
          searchable
          nothingFoundMessage="No services found"
          placeholder="Select service"
          data={
            availableServices?.map((service) => ({
              value: service.id,
              label: `${service.name}`,
            })) || []
          }
          value={formData.serviceId}
          onChange={handleServiceSelect}
          error={errors.serviceId}
          clearable
          styles={(theme) => ({
            input: {
              "&:focus": {
                borderColor: theme.colors.teal[5],
              },
            },
          })}
        />

        {/* Additional Questions Section */}
        <AdditionalQuestionsSection
          serviceId={formData.serviceId}
          activeAdjustmentForms={activeAdjustmentForms}
          selectedOptions={selectedAdjustmentOptions}
          onToggleOption={(optionId, questionId) =>
            handleAdjustmentOptionToggle(optionId, questionId)
          }
          isExpanded={isAdditionalQuestionsExpanded}
          onToggleExpanded={() =>
            setIsAdditionalQuestionsExpanded(!isAdditionalQuestionsExpanded)
          }
        />

        {formData.serviceId && (
          <Stack gap="md">
            <NumberInput
              label="Price ($)"
              required
              value={formData.price}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  price: Number(value) || 0,
                }))
              }
              min={0}
              step={0.01}
              decimalScale={2}
              fixedDecimalScale
              error={errors.price}
              styles={(theme) => ({
                input: {
                  "&:focus": {
                    borderColor: theme.colors.teal[5],
                  },
                },
              })}
            />
            {isPerUnitService && (
              <NumberInput
                label={`Units used${unitsLabel ? ` (${unitsLabel})` : ""}`}
                description="How many units/syringes were consumed. Pulled into the package redemption at checkout."
                value={formData.quantity}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    quantity: Number(value) || 0,
                  }))
                }
                min={0.01}
                step={0.5}
                decimalScale={2}
                error={errors.quantity}
              />
            )}
            <Group grow>
              <NumberInput
                label="Base Duration (minutes)"
                required
                value={formData.baseDuration}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    baseDuration: Number(value) || 30,
                  }))
                }
                min={5}
                max={600}
                step={5}
                error={errors.baseDuration}
                styles={(theme) => ({
                  input: {
                    "&:focus": {
                      borderColor: theme.colors.teal[5],
                    },
                  },
                })}
              />
              <NumberInput
                label="Processing Time (minutes)"
                value={formData.processingTime}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    processingTime: Number(value) || 0,
                  }))
                }
                min={0}
                max={600}
                step={5}
                styles={(theme) => ({
                  input: {
                    "&:focus": {
                      borderColor: theme.colors.teal[5],
                    },
                  },
                })}
              />
            </Group>

            <Group grow>
              <NumberInput
                label="Finishing Time (minutes)"
                value={formData.finishingTime}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    finishingTime: Number(value) || 0,
                  }))
                }
                min={0}
                max={600}
                step={5}
                styles={(theme) => ({
                  input: {
                    "&:focus": {
                      borderColor: theme.colors.teal[5],
                    },
                  },
                })}
              />
              <NumberInput
                label="Buffer Time (minutes)"
                value={formData.bufferTime}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    bufferTime: Number(value) || 0,
                  }))
                }
                min={0}
                max={600}
                step={5}
                styles={(theme) => ({
                  input: {
                    "&:focus": {
                      borderColor: theme.colors.teal[5],
                    },
                  },
                })}
              />
            </Group>
          </Stack>
        )}

        <Group w="100%" justify="space-between" gap="sm" mt="md">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!formData.staffId || !formData.serviceId}
          >
            {getButtonText()}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
